<template>
  <div
    ref="imageContainer"
    class="overflow-hidden rounded-sm group-hover:shadow-lg"
    :style="crop ? { overflow: 'hidden' } : {}">
    <LightboxViewer
      v-if="lightbox"
      :visible="isLightboxVisible"
      :imgs="effectiveLightboxImages"
      :index="lightboxIndex"
      @hide="isLightboxVisible = false" />
    <img
      v-if="haveImage"
      :src="activeImageUrl"
      class="object-cover relative flex-1 w-full max-w-full h-full aspect-portrait"
      :class="lightbox ? 'cursor-pointer' : ''"
      :style="getCropStyle()"
      @click="openLightbox" />
    <img
      v-else
      class="object-cover relative flex-1 w-full max-w-full h-full aspect-portrait"
      src="https://placehold.co/500x750/000000/FFFFFF/?text=No+Poster+Available" />

    <slot />
  </div>
</template>

<script setup>
import LightboxViewer from '@/components/LightboxViewer.vue'
import { computed, ref, onMounted, onUnmounted, watch } from 'vue'

const props = defineProps({
  path: String,
  lightbox: {
    type: Boolean,
    default: false
  },
  lightboxImages: {
    type: Array,
    default: null
  },
  crop: {
    type: Object,
    default: null
  }
})

// Lightbox state
const isLightboxVisible = ref(false)
const lightboxIndex = ref(0)
const imageContainer = ref(null)
const containerWidth = ref(0)

// Watch for container size changes
const updateContainerWidth = () => {
  if (imageContainer.value) {
    containerWidth.value = imageContainer.value.clientWidth
  }
}

// Set up resize observer to track container size changes
let resizeObserver = null

onMounted(() => {
  updateContainerWidth()

  // Use ResizeObserver to track container size changes
  if (window.ResizeObserver) {
    resizeObserver = new ResizeObserver(updateContainerWidth)
    if (imageContainer.value) {
      resizeObserver.observe(imageContainer.value)
    }
  }

  // Fallback to window resize event if ResizeObserver is not available
  window.addEventListener('resize', updateContainerWidth)
})

onUnmounted(() => {
  if (resizeObserver) {
    resizeObserver.disconnect()
  }
  window.removeEventListener('resize', updateContainerWidth)
})

const haveImage = computed(() => {
  return props.path !== undefined && props.path !== null
})

// Base TMDB image URL
const tmdbBaseUrl = 'https://image.tmdb.org/t/p/'

// Use lightboxImages prop if provided, otherwise fall back to single image
const effectiveLightboxImages = computed(() => {
  if (props.lightboxImages?.length) return props.lightboxImages
  return [fullSizeImageUrl.value]
})

// URL for full size image used in lightbox
const fullSizeImageUrl = computed(() => {
  if (haveImage.value) return `${tmdbBaseUrl}original${props.path}`
  return 'https://placehold.co/500x750/000000/FFFFFF/?text=No+Poster+Available'
})

// Calculate appropriate image size based on container width
const activeImageUrl = computed(() => {
  const dpr = window.devicePixelRatio || 1
  const requiredWidth = Math.ceil(containerWidth.value * dpr)

  // Available sizes: w92, w154, w185, w342, w500, w780, original
  let tmdbSize

  if (requiredWidth <= 92) {
    tmdbSize = 'w92'
  } else if (requiredWidth <= 154) {
    tmdbSize = 'w154'
  } else if (requiredWidth <= 185) {
    tmdbSize = 'w185'
  } else if (requiredWidth <= 342) {
    tmdbSize = 'w342'
  } else if (requiredWidth <= 500) {
    tmdbSize = 'w500'
  } else if (requiredWidth <= 780) {
    tmdbSize = 'w780'
  } else {
    tmdbSize = 'original'
  }

  if (haveImage.value) {
    return `${tmdbBaseUrl}${tmdbSize}${props.path}`
  }

  return 'https://placehold.co/500x750/000000/FFFFFF/?text=No+Poster+Available'
})

// Apply crop transform if crop data is provided
const getCropStyle = () => {
  if (!props.crop) return {}

  const { x, y, width, height, scale } = props.crop
  // Calculate transform to show the cropped area
  // x, y, width, height are normalized 0-1 values
  // For a poster aspect ratio, we need to calculate the actual crop area
  const scaleValue = scale || 1.0

  // Calculate the crop area in percentage
  // The image needs to be scaled and positioned to show only the crop area
  const cropLeft = x * 100
  const cropTop = y * 100
  const cropWidth = width * 100
  const cropHeight = height * 100

  // Calculate transform to position the crop area
  // We need to scale the image and translate it so the crop area fills the container
  const imageScale = 100 / (cropWidth * scaleValue)
  const translateX = -(cropLeft * imageScale)
  const translateY = -(cropTop * imageScale)

  return {
    transform: `translate(${translateX}%, ${translateY}%) scale(${imageScale})`,
    transformOrigin: 'top left',
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    objectPosition: 'top left'
  }
}

// Open lightbox at the selected index for large screens
const openLightbox = (e) => {
  if (props.lightbox) {
    isLightboxVisible.value = true
    e.stopPropagation()
  }
}
</script>
